/**
 * Users Three — Iconimate (https://iconimate.app/r/users-three.json, version eb17df386510).
 * Animation code: copyright (c) 2026 Muhammad Ammar (smammar100), MIT.
 * Glyph geometry: Phosphor Icons, copyright (c) 2023 Phosphor Icons, MIT.
 * Shared helpers moved to ./core; motion → m (strict LazyMotion). See LICENSE.md.
 */
import { forwardRef, useImperativeHandle } from 'react';
import { m } from 'motion/react';
import type { Variants } from 'motion/react';
import { RETURN_TRANSITION, staged, useHover } from './core';
import type { AnimatedIconHandle, AnimatedIconProps } from './core';

// WAVE — each head tips one way, swings through to the other, and centres, and
// the whole motion travels along the row: left, then centre, then right. Every
// head pivots at its neck (where it meets its shoulders), so the joint never
// opens. Tilt angles differ so the crowns travel the same distance.

/** The glyph's own six elements, untouched. */
const HEAD_L = 'M64,120A32,32,0,1,1,95,80';
const HEAD_R = 'M161,80a32,32,0,1,1,31,40';
const HEAD_C = { cx: 128, cy: 144, r: 40 };
const SH_L = 'M16,144a59.91,59.91,0,0,1,48-24';
const SH_R = 'M192,120a59.91,59.91,0,0,1,48,24';
const SH_C = 'M72,216a65,65,0,0,1,112,0';

/** The mark is stroke-only; it is never filled. */
const STROKE = {
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 16,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
};

const AT = (x: number, y: number) => ({
  transformBox: 'view-box' as const,
  originX: x / 256,
  originY: y / 256,
});
/** Neck pivots — each is the exact point where that head meets its shoulders. */
const NECK_L = AT(64, 120);
const NECK_C = AT(128, 184);
const NECK_R = AT(192, 120);

/** Matched crown travel, not matched angle. */
const TILT_SIDE = 17;
const TILT_CENTRE = 14;

const wave = (i: number, tilt: number): Variants => ({
  normal: { rotate: 0, transition: RETURN_TRANSITION },
  animate: {
    rotate: [0, tilt, tilt, -tilt, -tilt, 0],
    transition: {
      duration: 0.9,
      ease: 'easeInOut',
      times: [0, 0.18, 0.32, 0.58, 0.7, 1],
      delay: staged(i),
    },
  },
});
const waveL = wave(0, TILT_SIDE);
const waveC = wave(1, TILT_CENTRE);
const waveR = wave(2, TILT_SIDE);

export const UsersThreeIcon = forwardRef<AnimatedIconHandle, AnimatedIconProps>(function UsersThreeIcon(
  { size = 24, style, ...props },
  ref,
) {
  const { controls, reduced, start, stop, bind } = useHover();
  useImperativeHandle(ref, () => ({ startAnimation: start, stopAnimation: stop }), [start, stop]);

  if (reduced) {
    return (
      <div {...props} {...bind} style={{ display: 'inline-flex', overflow: 'hidden', ...style }}>
        <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 256 256" fill="none">
          <path d={HEAD_L} {...STROKE} />
          <path d={SH_L} {...STROKE} />
          <circle cx={HEAD_C.cx} cy={HEAD_C.cy} r={HEAD_C.r} {...STROKE} />
          <path d={SH_C} {...STROKE} />
          <path d={HEAD_R} {...STROKE} />
          <path d={SH_R} {...STROKE} />
        </svg>
      </div>
    );
  }

  return (
    <div {...props} {...bind} style={{ display: 'inline-flex', overflow: 'hidden', ...style }}>
      <m.svg
        xmlns="http://www.w3.org/2000/svg"
        width={size}
        height={size}
        viewBox="0 0 256 256"
        fill="none"
        initial="normal"
        animate={controls}
        style={{ overflow: 'visible' }}
      >
        <m.path d={HEAD_L} {...STROKE} variants={waveL} style={NECK_L} />
        <path d={SH_L} {...STROKE} />
        <m.circle cx={HEAD_C.cx} cy={HEAD_C.cy} r={HEAD_C.r} {...STROKE} variants={waveC} style={NECK_C} />
        <path d={SH_C} {...STROKE} />
        <m.path d={HEAD_R} {...STROKE} variants={waveR} style={NECK_R} />
        <path d={SH_R} {...STROKE} />
      </m.svg>
    </div>
  );
});
